import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Briefcase, Building2, Package, ShoppingCart, Warehouse,
  AlertTriangle, CheckCircle2, ChevronRight, ShieldCheck,
  Calendar, Activity,
} from 'lucide-react';
import {
  clients, fournisseurs, equipements, commandes, entrepots,
  tracabilite, etapesCommande,
  formatDate, formatMontant,
} from '../data/mockData';

const Dashboard = () => {
  const navigate = useNavigate();

  const alertesFournisseurs = fournisseurs.filter((f) =>
    f.documents.some((d) => d.statut === 'a_renouveler')
  );

  const commandesEnCours = commandes.filter((c) => c.etape !== 'installation');
  const commandesTerminees = commandes.filter((c) => c.etape === 'installation');

  const kpis = [
    { label: 'Clients (Marches)', value: clients.length, icon: Briefcase, color: 'sky', path: '/clients' },
    { label: 'Fournisseurs', value: fournisseurs.length, icon: Building2, color: 'sky', path: '/fournisseurs' },
    { label: 'Equipements references', value: equipements.length, icon: Package, color: 'sky', path: '/equipements' },
    { label: 'Commandes en cours', value: commandesEnCours.length, icon: ShoppingCart, color: 'amber', path: '/commandes' },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Tableau de bord</h1>
          <p className="page-subtitle">Vue d ensemble ERP Stock et Tracabilite</p>
        </div>
        <div className="header-date">
          <Calendar size={16} />
          <span>{new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
        </div>
      </div>

      <div className="kpi-grid kpi-grid-4">
        {kpis.map((kpi) => {
          const Icon = kpi.icon;
          return (
            <button key={kpi.label} className={`kpi-card kpi-${kpi.color}`} onClick={() => navigate(kpi.path)}>
              <div className="kpi-icon">
                <Icon size={22} />
              </div>
              <div className="kpi-body">
                <span className="kpi-label">{kpi.label}</span>
                <span className="kpi-value">{kpi.value}</span>
              </div>
              <ChevronRight size={16} className="kpi-arrow" />
            </button>
          );
        })}
      </div>

      {alertesFournisseurs.length > 0 && (
        <div className="alert-banner">
          <div className="alert-banner-icon">
            <AlertTriangle size={22} />
          </div>
          <div className="alert-banner-body">
            <h3>Documents fournisseurs a renouveler</h3>
            <p>
              {alertesFournisseurs.length} fournisseur(s) ont des documents legaux (KBIS, contrat sous-traitance...) approchant de leur date d expiration.
            </p>
          </div>
          <button className="btn-ghost" onClick={() => navigate('/fournisseurs')}>
            Consulter <ChevronRight size={14} />
          </button>
        </div>
      )}

      <div className="dashboard-grid">
        <section className="card">
          <header className="card-header">
            <div className="card-title">
              <ShoppingCart size={18} />
              <h2>Cycle des commandes fournisseurs</h2>
            </div>
            <button className="btn-link" onClick={() => navigate('/commandes')}>
              Tout voir <ChevronRight size={14} />
            </button>
          </header>
          <div className="card-body">
            <ul className="pipeline">
              {etapesCommande.map((etape, idx) => {
                const count = commandes.filter((c) => c.etape === etape.id).length;
                return (
                  <li key={etape.id} className="pipeline-item">
                    <span className="pipeline-index">{idx + 1}</span>
                    <div className="pipeline-body">
                      <strong>{etape.label}</strong>
                      <span>{etape.description}</span>
                    </div>
                    <span className="pipeline-count">{count}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>

        <section className="card">
          <header className="card-header">
            <div className="card-title">
              <Warehouse size={18} />
              <h2>Capacite des entrepots</h2>
            </div>
            <button className="btn-link" onClick={() => navigate('/stocks')}>
              Tout voir <ChevronRight size={14} />
            </button>
          </header>
          <div className="card-body">
            {entrepots.map((ent) => {
              const pct = Math.round((ent.nbEquipements / ent.capacite) * 100);
              return (
                <div key={ent.id} className="entrepot-row">
                  <div className="entrepot-row-head">
                    <div>
                      <p className="entrepot-name">{ent.nom}</p>
                      <p className="entrepot-sub">Responsable: {ent.responsable}</p>
                    </div>
                    <span className="entrepot-count">{ent.nbEquipements} / {ent.capacite}</span>
                  </div>
                  <div className="progress">
                    <div className="progress-bar" style={{ width: `${Math.min(pct, 100)}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      <div className="dashboard-grid" style={{ marginTop: '1.5rem' }}>
        <section className="card">
          <header className="card-header">
            <div className="card-title">
              <ShieldCheck size={18} />
              <h2>Tracabilite finalisee (N serie + BL + chantier)</h2>
            </div>
          </header>
          <div className="card-body">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Equipement</th>
                  <th>N serie</th>
                  <th>BL</th>
                  <th>Chantier</th>
                  <th>Etape</th>
                </tr>
              </thead>
              <tbody>
                {tracabilite.map((t) => (
                  <tr key={t.numeroSerie}>
                    <td>{t.equipement}</td>
                    <td><span className="mono">{t.numeroSerie}</span></td>
                    <td><span className="mono">{t.bl}</span></td>
                    <td>{t.chantier}</td>
                    <td>
                      <span className="badge badge-ok">
                        <CheckCircle2 size={12} /> {t.etape}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="card">
          <header className="card-header">
            <div className="card-title">
              <Activity size={18} />
              <h2>Avancement commandes recentes</h2>
            </div>
          </header>
          <div className="card-body">
            {commandes.slice(0, 4).map((cmd) => (
              <div key={cmd.id} className="cmd-row" onClick={() => navigate('/commandes')}>
                <div>
                  <p className="cmd-row-id">{cmd.id}</p>
                  <p className="cmd-row-sub">{cmd.chantier}</p>
                </div>
                <div className="cmd-row-right">
                  <span className="mono">{formatMontant(cmd.montantHT)}</span>
                  <span className={`badge badge-${cmd.etape === 'installation' ? 'ok' : 'info'}`}>
                    {etapesCommande.find((e) => e.id === cmd.etape)?.label}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <p className="meta-line" style={{ marginTop: '1.5rem' }}>
        {commandesTerminees.length} commande(s) terminees -
        derniere activite: {formatDate(commandes[0]?.date)}
      </p>
    </div>
  );
};

export default Dashboard;
